import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Check, ChevronsUpDown, Plus, X } from 'lucide-react';
import { useOpensUp } from './useOpensUp';
import { controlClass, isHexColor, Swatch } from '../ui';
import { TaxonomyItem } from '../../services/taxonomiesService';

import { foldText } from '../../utils/fuzzySearch';
/** Stored values may be a taxonomy key (`political-actor`) or its label (`Political Actor`) */
const normalise = (value: string) => foldText(value).replace(/[\s_]+/g, '-');
const matches = (option: TaxonomyItem, value: string) => {
  const v = normalise(value);
  return !!v && (normalise(option.key || '') === v || normalise(option.label) === v);
};

interface CategoryComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: TaxonomyItem[];
  error?: string;
  placeholder?: string;
  name?: string;
  id?: string;
}

export const CategoryCombobox: React.FC<CategoryComboboxProps> = ({
  value,
  onChange,
  options,
  error,
  placeholder = 'Select or type a category…',
  name = 'category',
  id = 'category',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Sync external value changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // If user typed something not matching an option, keep it as custom
        if (inputValue && inputValue !== value) {
          onChange(inputValue);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inputValue, value, onChange]);

  // Filter options based on input
  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) return options;
    const search = foldText(inputValue);
    const searchKey = normalise(inputValue);
    return options.filter(opt => foldText(opt.label).includes(search) || normalise(opt.key || '').includes(searchKey));
  }, [options, inputValue]);

  // Check if input matches any existing option exactly
  const isExactMatch = options.some(opt => matches(opt, inputValue));
  // Category colour of the typed/selected value, shown as a dot inside the field
  const valueColor = options.find(opt => matches(opt, inputValue))?.color;
  const showValueSwatch = isHexColor(valueColor);
  const showCustomOption = inputValue.trim() && !isExactMatch;
  // Keep labels aligned when only some categories have a colour
  const anyColored = options.some(opt => isHexColor(opt.color));

  const allItems = [
    ...filteredOptions,
    ...(showCustomOption ? [{ id: '__custom__', label: inputValue.trim(), key: inputValue.trim(), domain: 'stakeholder_category' as const }] : []),
  ];

  const handleSelect = (label: string) => {
    setInputValue(label);
    onChange(label);
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    setInputValue('');
    onChange('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault();
      setIsOpen(true);
      return;
    }

    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => (prev + 1) % allItems.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev <= 0 ? allItems.length - 1 : prev - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < allItems.length) {
          handleSelect(allItems[highlightedIndex].label);
        } else if (inputValue.trim()) {
          handleSelect(inputValue.trim());
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.children;
      if (items[highlightedIndex]) {
        (items[highlightedIndex] as HTMLElement).scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const opensUp = useOpensUp(isOpen && allItems.length > 0, inputRef, 268);

  return (
    <div className="relative font-ui" ref={containerRef}>
      <div className="relative">
        {showValueSwatch && (
          <Swatch color={valueColor} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" />
        )}
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsOpen(true);
            setHighlightedIndex(-1);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className={`${controlClass} h-10 pr-16 sm:h-9 ${showValueSwatch ? 'pl-7' : 'pl-3'}`}
          role="combobox"
          aria-invalid={error ? true : undefined}
          aria-expanded={isOpen}
          aria-controls="category-listbox"
          aria-autocomplete="list"
        />
        <div className="absolute inset-y-0 right-1 flex items-center">
          {inputValue && (
            <button
              type="button"
              onClick={handleClear}
              aria-label="Clear"
              className="inline-flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-ink"
              tabIndex={-1}
            >
              <X className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setIsOpen(!isOpen);
              if (!isOpen) inputRef.current?.focus();
            }}
            aria-label="Toggle options"
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 transition-colors hover:text-ink"
            tabIndex={-1}
          >
            <ChevronsUpDown className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      {isOpen && allItems.length > 0 && (
        <ul
          ref={listRef}
          id="category-listbox"
          role="listbox"
          className={`absolute z-30 ${opensUp ? 'bottom-full mb-1.5' : 'mt-1.5'} max-h-64 w-full animate-fade-up overflow-auto overscroll-contain rounded-2xl bg-white p-1.5 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.2)] ring-1 ring-black/[0.06]`}
        >
          {filteredOptions.map((option, index) => {
            const isSelected = matches(option, value || '');
            const isHighlighted = index === highlightedIndex;
            return (
              <li
                key={option.id}
                role="option"
                aria-selected={isSelected}
                onClick={() => handleSelect(option.label)}
                className={`flex min-h-[40px] cursor-pointer items-center justify-between gap-2 rounded-xl px-3 text-[13px] text-ink transition-colors sm:min-h-[36px] ${
                  isHighlighted ? 'bg-neutral-100' : 'hover:bg-neutral-100'
                }`}
              >
                <span className="flex min-w-0 items-center gap-2">
                  {isHexColor(option.color) ? (
                    <Swatch color={option.color} />
                  ) : (
                    anyColored && <span className="w-2 flex-shrink-0" aria-hidden="true" />
                  )}
                  <span className={`truncate ${isSelected ? 'font-medium' : ''}`}>{option.label}</span>
                </span>
                {isSelected && <Check className="h-4 w-4 flex-shrink-0 text-primary-600" strokeWidth={2} />}
              </li>
            );
          })}

          {showCustomOption && (
            <li
              role="option"
              aria-selected={highlightedIndex === filteredOptions.length}
              onClick={() => handleSelect(inputValue.trim())}
              className={`flex min-h-[40px] cursor-pointer items-center gap-2 rounded-xl px-3 text-[13px] text-ink transition-colors sm:min-h-[36px] ${
                highlightedIndex === filteredOptions.length ? 'bg-neutral-100' : 'hover:bg-neutral-100'
              }`}
            >
              <Plus className="h-4 w-4 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
              <span className="min-w-0 flex-1 truncate">{inputValue.trim()}</span>
              <span className="flex-shrink-0 text-[12px] text-neutral-400">Custom</span>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};
